import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
} from 'rxjs';

import { ShareableDatablockRequester } from '../../requesters/exposition/shareable-datablock.requester';
import { SubscriptionRequester } from '../../requesters/subscription/subscription.requester';
import { SubscriptionConfigurationStepperComponent } from '../components/subscription-configuration-stepper/subscription-configuration-stepper.component';
import { ProjectSubscriptionAdapter } from '../project-subscription.adapter';
import {
	fetchAvailableProjectsFail,
	fetchAvailableProjectsSuccess,
	fetchDatablockColumnsFail,
	fetchDatablockColumnsSuccess,
	fetchSourceColumnsFail,
	fetchSourceColumnsSuccess,
	fetchSubscriptionDataBlocksFail,
	fetchSubscriptionDataBlocksSuccess,
	getAvailableProjects,
	getDatablockColumns,
	getSourceColumns,
	initSubscriptionStepper,
	navigateToSubscriptionDetails,
	openCreateNewSubscriptionModal,
	openCreateNewSubscriptionModalFromDatablock,
	refreshAvailableProjects,
	refreshSourceColumns,
	refreshSubscriptionDatablockList,
	saveNewSubscription,
	saveNewSubscriptionFail,
	saveNewSubscriptionSuccess,
	SubscriptionActions,
} from './subscription.actions';

@Injectable()
export class SubscriptionCreatorEffects {
	// ##########################################################
	// Business Effects
	// ##########################################################
	public initSubscriptionStepper$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initSubscriptionStepper, refreshSubscriptionDatablockList),
			switchMap((action) =>
				this.shareableDatablockRequester.getShareableDatablocks(
					action.type === SubscriptionActions.InitSubscriptionConfig
				)
			),
			map((datablocks) =>
				fetchSubscriptionDataBlocksSuccess({
					datablocks:
						this.subscriptionAdapter.generateShareableDatablockList(datablocks),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionDataBlocksFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getDatablockColumns$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getDatablockColumns),
			switchMap((action) =>
				combineLatest([
					of(action.datablockId),
					this.shareableDatablockRequester.getDatablockColumns(
						action.datablockId,
						true
					),
				])
			),
			map(([datablockId, response]) =>
				fetchDatablockColumnsSuccess({
					datablockId,
					datablockColumns:
						this.subscriptionAdapter.generateDatablockColumns(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDatablockColumnsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getSourceColumns$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getSourceColumns, refreshSourceColumns),
			switchMap((action) =>
				this.shareableDatablockRequester.getDatablockColumns(
					action.datablockId,
					action.type === SubscriptionActions.GetSourceColumns
				)
			),
			map((response) =>
				fetchSourceColumnsSuccess({
					columns: this.subscriptionAdapter.generateAvailableColumns(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSourceColumnsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getAvailableProjects$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getAvailableProjects, refreshAvailableProjects),
			map(() =>
				fetchAvailableProjectsSuccess({
					projects: this.subscriptionAdapter.generateProjectList(),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchAvailableProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public saveNewSubscription$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(saveNewSubscription),
				switchMap((action) =>
					this.subscriptionRequester.createSubscription(
						action.datablockId,
						action.internalMetadata,
						action.publicMetadata,
						action.columns,
						action.profile,
						action.columnModel,
						action.filterModels
					)
				),
				tap((newSubscription) => {
					this.store.dispatch(
						saveNewSubscriptionSuccess({
							subscriptionId: newSubscription.id,
						})
					);
				}),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					this.store.dispatch(
						saveNewSubscriptionFail({
							error,
						})
					);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public afterSaveSubscriptionSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(saveNewSubscriptionSuccess),
			map((action) =>
				navigateToSubscriptionDetails({
					subscriptionId: action.subscriptionId,
				})
			)
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public createNewSubscriptionModal$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openCreateNewSubscriptionModal),
			switchMap(() => {
				const modalRef = this.modalService.openModal(
					SubscriptionConfigurationStepperComponent,
					{
						title: $localize`:i18n=@@subscription.config.create.title:`,
						subTitle: $localize`:i18n=@@subscription.config.create.subTitle:`,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([modalRef.afterClosed()]);
			}),
			filter(([modalResult]) => modalResult !== undefined),
			map(([modalResult]) => {
				const {
					datablockId,
					internalMetadata,
					publicMetadata,
					columns,
					profile,
					columnModel,
					filterModels,
				} = modalResult;

				return saveNewSubscription({
					datablockId,
					internalMetadata,
					publicMetadata,
					columns,
					profile,
					columnModel,
					filterModels,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public createNewSubscriptionModalFromDatablock$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openCreateNewSubscriptionModalFromDatablock),
			switchMap((action) => {
				const modalRef = this.modalService.openModal<{
					datablockId: number;
					datablockLabel: string;
				}>(
					SubscriptionConfigurationStepperComponent,
					{
						title: $localize`:i18n=@@subscription.config.create.title:`,
						subTitle: $localize`:i18n=@@subscription.config.create.subTitle:`,
						datablockId: action.datablockId,
						datablockLabel: action.datablockLabel,
					},
					{
						disableClose: true,
					}
				);
				return modalRef.afterClosed();
			}),
			filter((modalResult) => modalResult !== undefined),
			map((modalResult) => {
				const {
					datablockId,
					internalMetadata,
					publicMetadata,
					columns,
					profile,
					columnModel,
					filterModels,
				} = modalResult;

				return saveNewSubscription({
					datablockId,
					internalMetadata,
					publicMetadata,
					columns,
					profile,
					columnModel,
					filterModels,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly subscriptionRequester: SubscriptionRequester,
		private readonly shareableDatablockRequester: ShareableDatablockRequester,
		private readonly subscriptionAdapter: ProjectSubscriptionAdapter,
		private readonly modalService: AppModalService
	) {}
}
